/**
 * Title Field Options
 * Ported from breitling-storefront-next/src/components/FormFields/TitleField/utils.ts
 */

import { BRAND } from '@/lib/const';
import { BRAND_TRAITS, getBrandTraits } from '@/lib/brands/traits';

export const TITLE_VALUES = ['Mr', 'Mrs', 'Ms', 'NOTTOSAY'] as const;
export type TitleValue = (typeof TITLE_VALUES)[number];

export const PREFER_NOT_TO_SAY: TitleValue = 'NOTTOSAY';

/** Markets where Mrs (Sig.ra / Sra.) replaces Ms — BEC-24708 */
const MRS_COUNTRIES = new Set(['IT', 'ES']);

export interface TitleOption {
  label: string;
  value: string;
}

/**
 * Get the user title label. The label is placed before the user's name
 * @returns Empty string if no option available. Otherwise, the label with an ending empty space `" "`.
 */
export const getUserTitleLabel = (
  countryCode?: string,
  option?: {
    label?: string | null;
    value?: string | null;
  } | null
): string => {
  if (countryCode?.toUpperCase() === 'JP') return '';
  if (option?.value && ['Mr', 'Mrs', 'Ms'].includes(option?.value)) {
    return option?.label ? option.label + ' ' : '';
  }

  return '';
};

function getTitleOptionLabel(
  countryCode: string | undefined,
  t: (key: string) => string,
  value: TitleValue
): string {
  const isJapan = countryCode?.toUpperCase() === 'JP';
  switch (value) {
    case 'Mr':
      return t(isJapan ? 'male' : 'mr');
    case 'Mrs':
      return t('mrs');
    case 'Ms':
      return t(isJapan ? 'female' : 'ms');
    case 'NOTTOSAY':
      return t('preferNotToSay');
  }
}

function buildMarketTitleOptions(
  countryCode: string | undefined,
  t: (key: string) => string
): TitleOption[] {
  const normalizedCountry = countryCode?.toUpperCase();
  const isJapan = normalizedCountry === 'JP';
  const isMrsMarket = MRS_COUNTRIES.has(normalizedCountry ?? '');

  const options: TitleOption[] = [
    {
      label: getTitleOptionLabel(countryCode, t, 'Mr'),
      value: 'Mr',
    },
  ];

  if (!isJapan && BRAND_TRAITS.formsTitle.includesMrsInTitleOptions) {
    options.push({ label: getTitleOptionLabel(countryCode, t, 'Mrs'), value: 'Mrs' });
  }

  if (isJapan || BRAND_TRAITS.formsTitle.offersMsInMrsMarkets || !isMrsMarket) {
    options.push({ label: getTitleOptionLabel(countryCode, t, 'Ms'), value: 'Ms' });
  }

  options.push({
    label: getTitleOptionLabel(countryCode, t, PREFER_NOT_TO_SAY),
    value: PREFER_NOT_TO_SAY,
  });

  return options;
}

/**
 * Get title options based on country code.
 * Japan shows Male/Female instead of Mr/Mrs/Ms.
 */
export const getTitleOptions = (
  countryCode: string | undefined,
  t: (key: string) => string
): TitleOption[] => {
  return buildMarketTitleOptions(countryCode, t);
};

/**
 * Get the label for the title/gender field based on country.
 * Japan uses "Gender" (性別) while all other countries use "Title".
 */
export const getTitleFieldLabel = (
  countryCode: string | undefined,
  t: (key: string) => string
): string => {
  const isJapan = countryCode?.toUpperCase() === 'JP';
  return t(isJapan ? 'gender' : 'title');
};

const LEGACY_TITLE_MAP: Record<string, TitleValue> = {
  Miss: 'Ms',
  Dr: 'NOTTOSAY',
  prefer_not_to_say: 'NOTTOSAY',
};

/**
 * Normalize a stored title value to the current valid set.
 * Maps legacy values (Miss, Dr, prefer_not_to_say) to their closest current equivalent.
 * Returns undefined for unknown/empty values so the form treats them as unset.
 */
export const normalizeTitleValue = (value?: string | null): TitleValue | undefined => {
  if (!value) return undefined;
  if ((TITLE_VALUES as readonly string[]).includes(value)) return value as TitleValue;
  return LEGACY_TITLE_MAP[value];
};

const FEMALE_TITLE_ALIASES: Partial<Record<TitleValue, TitleValue>> = {
  Mrs: 'Ms',
  Ms: 'Mrs',
};

/**
 * Keep a stored title when this market offers it.
 * Breitling accounts may store Mrs; Gallet markets that only offer Ms show that instead.
 */
export const resolveStoredTitle = (
  value: string | null | undefined,
  countryCode: string | undefined
): TitleValue | undefined => {
  const normalized = normalizeTitleValue(value);
  if (!normalized) return undefined;

  const offered = new Set(getTitleOptions(countryCode, () => '').map((option) => option.value));
  if (offered.has(normalized)) return normalized;

  const alias = FEMALE_TITLE_ALIASES[normalized];
  if (alias && offered.has(alias)) return alias;

  return undefined;
};

/**
 * Map stored salutation onto a value the current market form can display.
 * On Gallet, legacy Breitling "Mrs" always maps to "Ms" (BEC-25439).
 */
export function resolveTitleForForm(
  value?: string | null,
  _countryCode?: string
): TitleValue | undefined {
  const normalized = normalizeTitleValue(value);
  if (!normalized) return undefined;
  if (!getBrandTraits(BRAND).formsTitle.mapsIncomingMrsToMs || normalized !== 'Mrs') {
    return normalized;
  }
  return 'Ms';
}

/** Same value for form display and outbound persistence on Gallet (BEC-25439). */
export function resolveTitleForSubmit(
  value?: string | null,
  countryCode?: string
): TitleValue | undefined {
  return resolveTitleForForm(value, countryCode);
}

/** String helper for controlled title state (checkout, address pickers). */
export function resolveTitleForFormValue(value?: string | null, countryCode?: string): string {
  return resolveTitleForForm(value, countryCode) ?? value ?? '';
}

/**
 * Get title option by value
 */
export const getTitleOptionByValue = (
  countryCode: string | undefined,
  t: (key: string) => string,
  value?: string | null
): TitleOption | null => {
  const forDisplay = resolveTitleForForm(value, countryCode);
  if (!forDisplay) return null;

  const marketOption = buildMarketTitleOptions(countryCode, t).find(
    (opt) => opt.value === forDisplay
  );
  if (marketOption) return marketOption;

  return { label: getTitleOptionLabel(countryCode, t, forDisplay), value: forDisplay };
};
